/**
 * InternalLinksSEO - SEO-focused internal linking component
 * Provides crawlable anchor tags for Google indexing
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';

// All active lotteries with their display names and regions
const ALL_LOTTERIES = [
  { code: 'euromillions', name: 'EuroMillions', region: 'Europe', flag: '🇪🇺' },
  { code: 'eurodreams', name: 'EuroDreams', region: 'Europe', flag: '🇪🇺' },
  { code: 'swisslotto', name: 'Swiss Lotto', region: 'Europe', flag: '🇨🇭' },
  { code: 'superenalotto', name: 'SuperEnalotto', region: 'Europe', flag: '🇮🇹' },
  { code: 'laprimitiva', name: 'La Primitiva', region: 'Europe', flag: '🇪🇸' },
  { code: 'german-lotto', name: 'German Lotto 6aus49', region: 'Europe', flag: '🇩🇪' },
  { code: 'uk-lotto', name: 'UK Lotto', region: 'Europe', flag: '🇬🇧' },
  { code: 'irish-lotto', name: 'Irish Lotto', region: 'Europe', flag: '🇮🇪' },
  { code: 'megasena', name: 'Mega-Sena', region: 'Americas', flag: '🇧🇷' },
  { code: 'powerball', name: 'Powerball', region: 'Americas', flag: '🇺🇸' },
  { code: 'megamillions', name: 'Mega Millions', region: 'Americas', flag: '🇺🇸' },
  { code: 'australian-lotto', name: 'Australian Lotto', region: 'Australia', flag: '🇦🇺' },
  { code: 'eurojackpot', name: 'EuroJackpot', region: 'Europe', flag: '🇪🇺' },
];

// Subpages for each lottery
const LOTTERY_SUBPAGES = [
  { path: '', label: 'Overview' },
  { path: '/results', label: 'Results' },
  { path: '/statistics', label: 'Statistics' },
  { path: '/frequency', label: 'Frequency' },
  { path: '/hot-cold', label: 'Hot & Cold' },
  { path: '/statistical-projections', label: 'Statistical Projections' },
];

/**
 * AllLotteriesLinks - Links to all lottery main pages
 * Use on hub pages (/en, /en/lotteries) for broad internal linking
 */
export const AllLotteriesLinks = ({ title = 'All Lotteries', className = '' }) => {
  const { lang = 'en' } = useParams();
  
  return (
    <nav className={`${className}`} aria-label="All lotteries navigation">
      <h2 className="text-lg font-bold text-white mb-3">{title}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {ALL_LOTTERIES.map((lottery) => (
          <Link
            key={lottery.code}
            to={`/${lang}/lottery/${lottery.code}`}
            className="bg-gray-900/40 border border-gray-700/50 rounded-lg px-3 py-2 hover:border-amber-500/50 transition-colors"
          >
            <span className="text-sm font-medium text-white">{lottery.name}</span>
            <span className="text-xs text-gray-400 ml-1">{lottery.flag}</span>
          </Link>
        ))}
      </div>
    </nav>
  );
};

/**
 * LotterySubpagesLinks - Links to all subpages of a specific lottery
 * Use on lottery detail pages for cross-linking
 */
export const LotterySubpagesLinks = ({ 
  lotteryCode, 
  lotteryName,
  currentPath = '',
  className = '' 
}) => {
  const { lang = 'en' } = useParams();
  
  return (
    <nav className={`${className}`} aria-label={`${lotteryName} page navigation`}>
      <h3 className="text-sm font-semibold text-amber-400 mb-2">
        Explore {lotteryName}
      </h3>
      <div className="flex flex-wrap gap-2">
        {LOTTERY_SUBPAGES.map((subpage) => {
          const isActive = currentPath === subpage.path;
          return (
            <Link
              key={subpage.path || 'overview'}
              to={`/${lang}/lottery/${lotteryCode}${subpage.path}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                isActive
                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                  : 'bg-gray-800/50 border-gray-700/50 text-gray-300 hover:border-amber-500/30 hover:text-white'
              }`}
            >
              {subpage.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

/**
 * RelatedLotteriesLinks - Links to other lotteries (excluding current)
 * Use on lottery detail pages for cross-lottery linking
 */
export const RelatedLotteriesLinks = ({ 
  currentLotteryCode,
  title = 'Other Lotteries',
  className = '' 
}) => {
  const { lang = 'en' } = useParams();
  const otherLotteries = ALL_LOTTERIES.filter(l => l.code !== currentLotteryCode);
  
  return (
    <nav className={`${className}`} aria-label="Related lotteries navigation">
      <h3 className="text-sm font-semibold text-amber-400 mb-2">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {otherLotteries.map((lottery) => (
          <Link
            key={lottery.code}
            to={`/${lang}/lottery/${lottery.code}`}
            className="text-xs px-3 py-1.5 rounded-full bg-gray-800/50 border border-gray-700/50 text-gray-300 hover:border-amber-500/30 hover:text-white transition-colors"
          >
            {lottery.name} {lottery.flag}
          </Link>
        ))}
      </div>
    </nav>
  );
};

/**
 * BreadcrumbNav - SEO breadcrumb with proper anchor tags
 * Use on all lottery pages for hierarchical linking
 */
export const BreadcrumbNav = ({ 
  items = [], // Array of { label, path } objects
  className = '' 
}) => {
  const { lang = 'en' } = useParams();
  
  return (
    <nav className={`text-sm ${className}`} aria-label="Breadcrumb">
      <ol className="flex items-center gap-2 text-gray-400">
        <li>
          <Link to={`/${lang}`} className="hover:text-white transition-colors">
            Home
          </Link>
        </li>
        <li>/</li>
        <li>
          <Link to={`/${lang}/lotteries`} className="hover:text-white transition-colors">
            Lotteries
          </Link>
        </li>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            <li>/</li>
            <li>
              {item.path ? (
                <Link to={item.path} className="hover:text-white transition-colors">
                  {item.label}
                </Link>
              ) : (
                <span className="text-white">{item.label}</span>
              )}
            </li>
          </React.Fragment>
        ))}
      </ol>
    </nav>
  );
};

/**
 * FooterLotteryLinks - Comprehensive footer links for all lotteries and subpages
 * Use in site footer for maximum internal linking coverage
 */
export const FooterLotteryLinks = ({ className = '' }) => {
  const { lang = 'en' } = useParams();
  
  // Group lotteries by region
  const europeGroup = ALL_LOTTERIES.filter(l => l.region === 'Europe');
  const americasGroup = ALL_LOTTERIES.filter(l => l.region === 'Americas');
  
  return (
    <nav className={`${className}`} aria-label="Footer lottery navigation">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Europe Lotteries */}
        <div>
          <h4 className="text-sm font-semibold text-amber-400 mb-3">Europe</h4>
          <ul className="space-y-1">
            {europeGroup.map((lottery) => (
              <li key={lottery.code}>
                <Link
                  to={`/${lang}/lottery/${lottery.code}`}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  {lottery.name} {lottery.flag}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Americas Lotteries */}
        <div>
          <h4 className="text-sm font-semibold text-amber-400 mb-3">Americas</h4>
          <ul className="space-y-1">
            {americasGroup.map((lottery) => (
              <li key={lottery.code}>
                <Link
                  to={`/${lang}/lottery/${lottery.code}`}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  {lottery.name} {lottery.flag}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Quick Links */}
        <div>
          <h4 className="text-sm font-semibold text-amber-400 mb-3">Quick Links</h4>
          <ul className="space-y-1">
            <li>
              <Link to={`/${lang}`} className="text-sm text-gray-400 hover:text-white transition-colors">
                Home
              </Link>
            </li>
            <li>
              <Link to={`/${lang}/lotteries`} className="text-sm text-gray-400 hover:text-white transition-colors">
                All Lotteries
              </Link>
            </li>
            <li>
              <Link to={`/${lang}/predictions`} className="text-sm text-gray-400 hover:text-white transition-colors">
                Predictions
              </Link>
            </li>
            <li>
              <Link to={`/${lang}/statistics`} className="text-sm text-gray-400 hover:text-white transition-colors">
                Statistics
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default {
  AllLotteriesLinks,
  LotterySubpagesLinks,
  RelatedLotteriesLinks,
  BreadcrumbNav,
  FooterLotteryLinks,
  ALL_LOTTERIES,
  LOTTERY_SUBPAGES,
};
